/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useEffect, useId, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import { useIsReducedMotion } from '@atlaskit/motion/use-is-reduced-motion';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

// The animated prism palette has no equivalent design tokens.
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
const palettes = {
	light: { blue: '#1D7AFC', purple: '#BF63F3', yellow: '#FCA700' },
	dark: { blue: '#0055CC', purple: '#BF63F3', yellow: '#FCA700' },
};
const staticBorder = css({
	position: 'relative',
	isolation: 'isolate',
	borderRadius: token('radius.large'),
	boxShadow: token('elevation.shadow.overlay'),
	'&::after': {
		content: "''",
		position: 'absolute',
		inset: 0,
		zIndex: 2,
		borderRadius: 'inherit',
		paddingTop: token('border.width'),
		paddingRight: token('border.width'),
		paddingBottom: token('border.width'),
		paddingLeft: token('border.width'),
		background: `conic-gradient(from var(--gradient-angle, 45deg), ${token('color.rovo.border.saffron')} 0% 20.192%, ${token('color.rovo.border.lime')} 20.202% 46.637%, ${token('color.rovo.border.blue')} 46.647% 70.185%, ${token('color.rovo.border.purple')} 70.195% 100%)`,
		mask: 'linear-gradient(white, white) content-box, linear-gradient(white, white)',
		maskComposite: 'exclude',
		pointerEvents: 'none',
	},
	'&::before': {
		content: "''",
		position: 'absolute',
		zIndex: -1,
		inset: token('space.200'),
		borderRadius: 'inherit',
		background: `conic-gradient(from var(--gradient-angle, 45deg), ${token('color.rovo.border.saffron')} 0% 20.192%, ${token('color.rovo.border.lime')} 20.202% 46.637%, ${token('color.rovo.border.blue')} 46.647% 70.185%, ${token('color.rovo.border.purple')} 70.195% 100%)`,
		filter: 'blur(16px)',
		backfaceVisibility: 'hidden',
		transform: 'translate3d(0, 0, 0)',
	},
});
const lightBackground = css({
	background:
		'linear-gradient(to top left, #FCA700, transparent, #1D7AFC), linear-gradient(to top right, #BF63F3, transparent, #BF63F3) #BF63F3',
});
const darkBackground = css({
	background:
		'linear-gradient(to top left, #FCA700, transparent, #0055CC), linear-gradient(to top right, #BF63F3, transparent, #BF63F3) #BF63F3',
});
/* eslint-enable @atlaskit/design-system/ensure-design-token-usage */

const loadingBorder = css({
	display: 'flex',
	position: 'relative',
	borderRadius: token('radius.large'),
	boxShadow: token('elevation.shadow.overlay'),
});
const edgeFrame = css({
	position: 'absolute',
	inset: 0,
	zIndex: 2,
	borderRadius: token('radius.large'),
	paddingTop: token('border.width'),
	paddingRight: token('border.width'),
	paddingBottom: token('border.width'),
	paddingLeft: token('border.width'),
	mask: 'linear-gradient(white, white) content-box, linear-gradient(white, white)',
	maskComposite: 'exclude',
	pointerEvents: 'none',
});
const borderSvg = css({
	position: 'absolute',
	zIndex: 2,
	borderRadius: token('radius.large'),
	left: 0,
	top: 0,
	width: '100%',
	height: '100%',
	pointerEvents: 'none',
});
const glowSvg = css({
	zIndex: 0,
	width: 'calc(100% - 4px)',
	height: 'calc(100% - 4px)',
	top: token('space.050'),
	left: token('space.050'),
	filter: 'blur(8px)',
	backfaceVisibility: 'hidden',
	transform: 'translate3d(0, 0, 0)',
});
const content = css({
	position: 'relative',
	flexGrow: 1,
	zIndex: 1,

	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface.overlay'),
	minWidth: 0,
});
const edges = [
	{
		axis: 'x',
		start: ['0%', '400%'],
		end: ['400%', '800%'],
		x1: '0%',
		x2: '400%',
		y1: '0',
		y2: '0',
		path: 'M0 0h24',
	},
	{
		axis: 'y',
		start: ['-100%', '300%'],
		end: ['300%', '700%'],
		x1: '0',
		x2: '0',
		y1: '-100%',
		y2: '300%',
		path: 'M24 0v24',
	},
	{
		axis: 'x',
		start: ['300%', '-100%'],
		end: ['700%', '300%'],
		x1: '300%',
		x2: '700%',
		y1: '0',
		y2: '0',
		path: 'M24 24H0',
	},
	{
		axis: 'y',
		start: ['0%', '-400%'],
		end: ['400%', '0%'],
		x1: '0',
		x2: '0',
		y1: '0',
		y2: '400%',
		path: 'M0 24V0',
	},
];

const AnimatedEdges = ({ isGlowing = false }: { isGlowing?: boolean }) => {
	const id = useId();
	const svgRef = useRef<SVGSVGElement>(null);
	const isReducedMotion = useIsReducedMotion();
	const { colorMode } = useThemeObserver();
	const palette = colorMode === 'dark' ? palettes.dark : palettes.light;

	useEffect(() => {
		const svg = svgRef.current;
		if (isReducedMotion) {
			return;
		}
		const frame = requestAnimationFrame(() => {
			svg?.querySelectorAll('animate').forEach((animation) => animation.beginElement());
		});
		return () => {
			cancelAnimationFrame(frame);
			svg?.querySelectorAll('animate').forEach((animation) => animation.endElement());
		};
	}, [isReducedMotion]);

	const animatedSvg = (
		<svg
			ref={svgRef}
			css={[borderSvg, isGlowing && glowSvg]}
			viewBox="0 0 24 24"
			preserveAspectRatio="none"
			aria-hidden
			role="presentation"
			data-testid={isGlowing ? 'explanation-loading-glow' : 'explanation-loading-border'}
		>
			<defs>
				{edges.map((edge, index) => (
					<linearGradient
						key={edge.path}
						id={'rovo-' + id + '-' + index}
						gradientUnits="userSpaceOnUse"
						x1={edge.x1}
						x2={edge.x2}
						y1={edge.y1}
						y2={edge.y2}
						spreadMethod="reflect"
					>
						{!isReducedMotion && (
							<>
								<animate
									begin="indefinite"
									attributeName={edge.axis + '1'}
									from={edge.start[0]}
									to={edge.start[1]}
									repeatCount="indefinite"
									dur="3s"
								/>
								<animate
									begin="indefinite"
									attributeName={edge.axis + '2'}
									from={edge.end[0]}
									to={edge.end[1]}
									repeatCount="indefinite"
									dur="3s"
								/>
							</>
						)}
						{[palette.yellow, palette.blue, palette.purple, palette.blue, palette.yellow].map(
							(color, stop) => (
								<stop key={stop} offset={stop / 4} stopColor={color} />
							),
						)}
					</linearGradient>
				))}
			</defs>
			<g strokeWidth="8">
				{edges.map((edge, index) => (
					<path
						key={edge.path}
						stroke={'url(#rovo-' + id + '-' + index + ')'}
						d={edge.path}
						vectorEffect="non-scaling-stroke"
					/>
				))}
			</g>
		</svg>
	);

	return isGlowing ? animatedSvg : <div css={edgeFrame}>{animatedSvg}</div>;
};

export const RovoBorder = ({
	children,
	isLoading,
	isBorderless,
	testId,
}: {
	children: React.ReactNode;
	isBorderless: boolean;
	isLoading: boolean;
	testId?: string;
}): React.JSX.Element => {
	const { colorMode } = useThemeObserver();
	if (isBorderless) {
		return <>{children}</>;
	}
	return (
		<div
			css={[
				isLoading && loadingBorder,
				isLoading && colorMode === 'dark' && darkBackground,
				isLoading && colorMode !== 'dark' && lightBackground,
				!isLoading && staticBorder,
			]}
			data-testid={testId ?? 'explanation-border'}
		>
			{isLoading && (
				<>
					<AnimatedEdges />
					<AnimatedEdges isGlowing />
				</>
			)}
			<div css={content}>{children}</div>
		</div>
	);
};
